import React, { useContext, useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import IconButton from '@mui/joy/IconButton';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import ObjectShow from "@components/objects/ObjectShow";
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import ObjectColumn from "@components/objects/ObjectColumn";
import { ObjectPrimaryInfo } from "@components/objects/ObjectPrimaryInfo";
import Avatar from "@mui/joy/Avatar";
import { ObjectContext } from "@providers/ObjectProvider";
import { ensureDateObject, formatAbsoluteDate, formatDate, isValidDate } from "@utils/dates";
import ObjectPreview from "@components/objects/ObjectPreview";
import { generateAddress } from "@utils/generateAddress";
import { CardOverflow, Grid, LinearProgress, List, ListDivider, ListItem, ListItemContent, ListItemDecorator, Table, Checkbox } from "@mui/joy";
import AbstractObjectRelationship from "@components/objects/AbstractObjectRelationship";
import UserAccessCard from "@components/objects/UserAccessCard";
import { intervalToDuration } from "date-fns";
import ObjectEdit from "@components/objects/ObjectEdit";
import { ObjectPrimaryInfoEdit } from "@components/objects/ObjectPrimaryInfoEdit";
import { ObjectRelationshipEdit } from "@components/objects/ObjectRelationshipEdit";
import { countries, donationFrequencies, donationTypes, emailNotificationTypes } from "@utils/selectOptions";
import { ObjectRelationship } from "@components/objects/ObjectRelationship";
import SearchSelect from "@components/SearchSelect";
import { CheckPermission } from "@components/CheckPermission";
import { useDeleteBusiness, useGetBusiness, useUpdateBusiness } from "@api/businessService";
import { ContextualHelp } from "@components/ContextualHelp";
import { useDeleteProperty, useGetProperty, useUpdateProperty } from "@api/propertyService";
import { useDeleteDonation, useGetDonation, useUpdateDonation } from "@api/donationService";
import Subheader from "@components/layout/Subheader";
import { Form } from "@components/forms/Form";
import { useAuth } from "@providers/AuthProvider";
import { passwordStrength } from 'check-password-strength'
import toast from "react-hot-toast";
import { authenticatedApi } from "api";
import MaestroLoader from "@components/MaestroLoader";
import { FormProvider, useForm } from "react-hook-form";
import { useDocumentTitle } from "@uidotdev/usehooks";

const accountInfoFields = [
    {
        name: 'first_name',
        type: 'text',
        placeholder: 'Your first name',
        showRequired: (object) => true,
        config: {
            required: true,
        },
    },
    {
        name: 'last_name',
        type: 'text',
        placeholder: 'Your last name',
        showRequired: (object) => true,
        config: {
            required: true,
        },
    },
    {
        name: 'email',
        type: 'email',
        placeholder: 'Your email address',
        showRequired: (object) => true,
        config: {
            required: true,
        },
        content: (object) => (
            <Typography sx={{fontSize: 'sm', mt: 1}}>Note: This will change the email address you use to log in.</Typography>
        )
        // todo: helper text to tell them that it will change the email they log in with (esp if they have access to more than 1 maestro instance)
    },
    {
        type: 'submit',
        label: false,
        buttonLabel: 'Update Account',
    },
];

const passwordFields = [
    {
        name: 'current_password',
        type: 'password',
        showRequired: (object) => true,
        config: {
            required: true,
        },
    },
    {
        name: 'new_password',
        type: 'password',
        showRequired: (object) => true,
        config: {
            validate: (value, object) => {
                if (value != object.confirm_password) return 'Passwords must match';
                return true;
            },
        },
        content: (object) => {
            const value = object?.new_password ?? '';
            const strength = passwordStrength(value);
            const minLength = 10;

            const hueForStrengthLevel = {
                0: Math.min(0 * 10, 120), // too weak
                1: Math.min(4.5 * 10, 120), // weak
                2: Math.min(7 * 10, 120), // medium
                3: Math.min(10 * 10, 120), // strong
            }

            return (
            <Stack
                sx={{
                    mt: 1,
                    '--hue': hueForStrengthLevel[strength.id],// Math.min(value.length * 10, 120),
                }}
            >
                <LinearProgress
                    determinate
                    size="sm"
                    value={Math.min((value.length * 100) / minLength, 100)}
                    sx={{
                    bgcolor: 'background.level3',
                    color: 'hsl(var(--hue) 80% 40%)',
                    }}
                />
                <Typography
                    level="body-xs"
                    sx={{ alignSelf: 'flex-end', color: 'hsl(var(--hue) 80% 30%)' }}
                >
                    {strength.value}
                    {/*
                    {value.length < 3 && 'Very weak'}
                    {value.length >= 3 && value.length < 6 && 'Weak'}
                    {value.length >= 6 && value.length < 10 && 'Strong'}
                    {value.length >= 10 && 'Very strong'}
                    */}
                </Typography>
            </Stack>
        )},
    },
    {
        name: 'confirm_password',
        type: 'password',
        showRequired: (object) => true,
        config: {
            validate: (value, object) => {
                if (value != object.new_password) return 'Passwords must match';
                return true;
            },
        },
    },
    {
        type: 'submit',
        label: false,
        buttonLabel: 'Update Password',
    },
];

export default function Profile(props) {
    const { user, userMeta, setUser } = useAuth();
    useDocumentTitle(`Account | Maestro Community Manager`);

    if(!user) {
        return (<>
            <Subheader
                title={'My Account'}
                // subtitle={`${user.first_name} ${user.last_name} (${user.email})`}
                tint={'default'}
            >
            </Subheader>
            <MaestroLoader centered/>
        </>);
    }

    const onSubmitAccountInfo = (fields) => {
        const { first_name, last_name, email } = fields;
        const performUpdate = authenticatedApi.put(`/users/${user.id}`, {
            first_name, last_name, email,
        });
        toast.promise(performUpdate, {
            loading: `Saving...`,
            success: `Your account info was saved!`,
            error: (err) => err?.response?.data?.message?.includes('Duplicate entry') ? 'This email address is already in use.' : `There was an error while saving.`,
        });
        performUpdate.then(() => {
            setUser({
                ...user,
                first_name,
                last_name,
                email,
            });
        });
    };

    const onSubmitEmailPreferences = () => {
        //
    };

    return (<>
        <Subheader
            title={'My Account'}
            subtitle={`${user.first_name} ${user.last_name} (${user.email})`}
            tint={'default'}
        >
        </Subheader>

        <Box sx={theme => ({
            backgroundImage: `linear-gradient(180deg, ${theme.palette['default'][100]} 0%, white 100%);`,// : null,
            backgroundAttachment: 'fixed',
            minHeight: 'calc(100% - var(--Header-height))',
            p: 2,
        })}>
            <Grid container columns={12} gap={2}>
                <ObjectColumn>
                    <Form onSubmit={onSubmitAccountInfo} initialData={user}>
                        <ObjectPrimaryInfoEdit
                            title={'Update Account Info'}
                            tint={'default'}
                            fields={accountInfoFields}
                        />
                    </Form>
                    <UpdatePasswordBox/>
                </ObjectColumn>
                <ObjectColumn>
                    <UpdateEmailPreferencesBox/>
                </ObjectColumn>
            </Grid>
        </Box>
    </>);
}

function UpdatePasswordBox() {
    const { user, userMeta, setUser } = useAuth();

    const formMethods = useForm({
        // defaultValues: initialData ? initialData : async () => data,
    });
    const {
        reset,
        handleSubmit,
        formState: { isSubmitting, errors }
    } = formMethods;

    const onSubmitPasswordUpdate = (fields) => {
        const { current_password, new_password, confirm_password } = fields;
        const performUpdate = authenticatedApi.put(`/users/${user.id}`, {
            current_password, new_password//, confirm_password
        });
        toast.promise(performUpdate, {
            loading: `Saving...`,
            success: `Your password was updated!`,
            error: (err) => err?.response?.data?.message ?? `There was an error while saving.`,
        });
        performUpdate.then(() => {
            reset();
        });
    };

    return (
        <form onSubmit={handleSubmit(onSubmitPasswordUpdate)}>
            <FormProvider {...formMethods}>
                <ObjectPrimaryInfoEdit
                    title={'Update Password'}
                    tint={'default'}
                    fields={passwordFields}
                />
            </FormProvider>
        </form>
    );
}

const emailNotificationOptions = {
    created: 'New',
    modified: 'Modified',
    completed: 'Completed',
    access_granted: 'Assigned to me',
    task_overdue: 'Overdue',// 'Overdue Tasks',
    task_due_today: 'Due Today',// 'Tasks due today',
    task_due_soon: 'Due soon',// 'Tasks due soon',
    unread: 'Unread',
};

const availableEmailNotifications = [
    {
        name: 'Contacts',
        key: 'contact',
        options: ['created', 'modified']
    },
    {
        name: 'Work Plans',
        key: 'workplan',
        options: ['created', 'modified', 'access_granted']
    },
    {
        name: 'Tasks',
        key: 'workplan_task',
        options: ['access_granted', 'completed', 'task_overdue', 'task_due_today', 'task_due_soon']
    },
    {
        name: 'Businesses',
        key: 'business',
        options: ['created', 'modified', 'access_granted']
    },
    {
        name: 'Properties',
        key: 'property',
        options: ['created', 'modified', 'access_granted']
    },
    {
        name: 'Events',
        key: 'event',
        options: ['created', 'modified']
    },
    /*
    {
        name: 'Messages',
        key: 'messages',
        options: ['unread']
    },
    */
];

let generatedEmailPreferences = [];
for(const prefType of availableEmailNotifications) {
    let checkboxOptions = [];
    for(const opt of prefType.options) {
        checkboxOptions.push({
            value: prefType.key+"."+opt,
            label: emailNotificationOptions[opt],
            disabled: (object) => object.email_notifications_type !== 'all',
        })
    }

    generatedEmailPreferences.push({
        name: prefType.key,
        label: prefType.name,
        type: 'info',
        content: (values, setValue, watch) => {
            return <Box sx={{display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 1}}>
                {checkboxOptions.map(option => {
                    const checked = !!watch(option.value);
                    return (
                    <Checkbox
                        key={option.value}
                        name={option.value}
                        label={option.label}
                        checked={checked}
                        onChange={() => {
                            setValue(option.value, !checked);
                        }}
                        disabled={values.email_notifications_type !== 'all'}
                    />
                )})}
            </Box>
        },
        // content: (object) => {
        // options: checkboxOptions,
    });
}

const emailPreferencesFields = [
    {
        type: 'info',
        label: false,
        content: () => <Typography>
            Check the boxes for each of the email notifications you wish to receive.
            <br/>
            <b>Note:</b> These checkboxes only apply if "email type" is set to "all".
        </Typography>
    },
    {
        name: 'email_notifications_type',
        label: <>Email Type</>,
        type: 'select',
        options: emailNotificationTypes,
    },
    ...generatedEmailPreferences,
    {
        type: 'submit',
        label: false,
        buttonLabel: 'Update Preferences',
    },
];

function UpdateEmailPreferencesBox() {
    const { user, userMeta, getUserMeta, setUser } = useAuth();
    const [defaultValues, setDefaultValues] = useState({});
    const [metaIds, setMetaIds] = useState({
        email_notifications_type: null,
        email_notifications: null,
    });

    const formMethods = useForm({
        // defaultValues: initialData ? initialData : async () => data,
    });
    const {
        setValue,
        reset,
        handleSubmit,
        watch,
        formState: { isSubmitting, errors }
    } = formMethods;

    // make sure we have user meta
    useEffect(() => {
      getUserMeta();
    }, []);

    // set the default values
    useEffect(() => {
        if(!userMeta) {
            return;
        }

        const typePref = userMeta.data.find(entry => entry.meta_key === 'email_notifications_type');
        const optionPref = userMeta.data.find(entry => entry.meta_key === 'email_notifications');

        setMetaIds({
            email_notifications_type: typePref?.id,
            email_notifications: optionPref?.id,
        });

        const optionVals = optionPref ? JSON.parse(optionPref.meta_value) : {};

        let _defaultValues = {
            email_notifications_type: typePref?.meta_value ?? 'none',
            ...optionVals,
        };
        setDefaultValues(_defaultValues);
        reset(_defaultValues);
        // setValue('email_notifications_type', _defaultValues.email_notifications_type);
        // setValue('email_notifications', _defaultValues.email_notifications_type);
    }, [userMeta]);

    if(!userMeta) {
        return null;
    }

    const onSubmit = (fields) => {
        const { email_notifications_type, ...restFields } = fields;

        const performTypeUpdate = authenticatedApi.put(`/user-meta/${metaIds.email_notifications_type}`, {
            meta_value: email_notifications_type,
        });
        const performOptionUpdate = authenticatedApi.put(`/user-meta/${metaIds.email_notifications}`, {
            meta_value: restFields,
        });

        const performUpdate = Promise.all([performTypeUpdate, performOptionUpdate]);

        toast.promise(performUpdate, {
            loading: `Saving...`,
            success: `Your preferences were updated!`,
            error: (err) => err?.response?.data?.message ?? `There was an error while saving.`,
        });
        performUpdate.then(() => {
            // reset();
        });
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} initialData={userMeta}>
            <FormProvider {...formMethods}>
                <ObjectPrimaryInfoEdit
                    title={'Email Preferences'}
                    tint={'default'}
                    fields={emailPreferencesFields}
                />
            </FormProvider>
        </form>
    );

}
